import { Directive, EventEmitter, forwardRef, Input } from '@angular/core';
import {
  GridRowNode,
  GridRowPending,
  isGridRowGroup,
} from '@assetaccountant/core/components/grid/grid-table/grid-table.component';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  GetRowDataParams,
  GetRowDataResult,
  GRID_ROW_DATA,
  GridRowDataProvider,
} from './grid-row-data.provider';

export interface ServerRowDataParams<T extends {}> {
  top: number;
  parent?: T;
}

export interface ServerRowDataResult<T extends {}> {
  rows: T[];
  count: number | null;
}

export interface ServerRowDataConfig<T extends {}, U extends T = T> {
  getRowData(params: ServerRowDataParams<U>): Observable<ServerRowDataResult<T>>;
  refresh: EventEmitter<{ purge: boolean }>;
  readonly infiniteScroll?: boolean;
  bucketSize: number;

  groupConfig?: {
    isGroup(row: T): row is U;
    getGroupKey(rowGroup: U): string;
    getGroupCount(rowGroup: U): number | null;
  };
}

@Directive({
  selector: '[serverRowData]',
  providers: [{ provide: GRID_ROW_DATA, useExisting: forwardRef(() => ServerRowDataDirective) }],
  standalone: false,
})
export class ServerRowDataDirective<T extends {}, U extends T> implements GridRowDataProvider<T> {
  @Input() serverRowData: ServerRowDataConfig<T, U>;

  get refresh() {
    return this.serverRowData.refresh;
  }

  get bucketSize() {
    return this.serverRowData.bucketSize;
  }

  get infiniteScroll() {
    return this.serverRowData.infiniteScroll ?? false;
  }

  private infiniteCount?: number;

  constructor() {}

  getRowData({ parent, top }: GetRowDataParams<T>): Observable<GetRowDataResult<T>> {
    const { getRowData, groupConfig } = this.serverRowData;

    let newRowCount: number | null;
    let totalRowCount: number | null;
    if (!parent && this.infiniteScroll && top && this.infiniteCount) {
      newRowCount = Math.min(this.bucketSize, this.infiniteCount - top);
      totalRowCount = this.infiniteCount;
    } else if (parent?.count) {
      top -= top % this.bucketSize;
      newRowCount = parent.count - top;
      totalRowCount = parent.count;
    } else {
      newRowCount = null;
      totalRowCount = null;
    }

    if (newRowCount && totalRowCount) {
      let rowNodes: Array<GridRowNode<T>>;
      if (parent?.children) {
        const existingRows = parent.children.filter(
          (row): row is GridRowPending =>
            !isGridRowGroup(row) &&
            row.state !== 'LOADED' &&
            top <= row.groupIndex &&
            row.groupIndex < top + this.bucketSize,
        );
        existingRows.forEach(row => {
          row.state = 'LOADING';
        });
        rowNodes = existingRows;
      } else {
        rowNodes = Array.from(
          { length: newRowCount },
          (_, i): GridRowPending => ({
            state: 'LOADING',
            groupKey: parent?.key,
            groupIndex: top + i,
            data: undefined,
          }),
        );
      }

      return new Observable<GetRowDataResult<T>>(observer => {
        observer.next({ rows: rowNodes, count: totalRowCount });

        return getRowData({
          top,
          parent: parent?.groupData as U,
        }).subscribe({
          next: ({ rows: newRows }) => {
            rowNodes.forEach((existingRow, i) => {
              const newRow = newRows[i];
              if (newRow) {
                Object.assign(existingRow, toRowNode(newRow, top + i));
                if (isGridRowGroup(existingRow)) {
                  delete (existingRow as any).groupKey;
                  delete (existingRow as any).groupIndex;
                  delete (existingRow as any).state;
                }
              } else if (!isGridRowGroup(existingRow)) {
                existingRow.state = 'LAZY';
              }
            });

            observer.complete();
          },
          error: err => observer.error(err),
        });
      });
    }

    if (this.infiniteScroll && !parent && !top) {
      this.infiniteCount = undefined;
    }

    return getRowData({
      top,
      parent: parent?.groupData as U,
    }).pipe(
      map(({ rows, count }) => {
        if (this.infiniteScroll && !parent && !top) {
          this.infiniteCount = count && rows.length < count ? count : undefined;
        }

        return {
          rows: rows.map((row, i) => toRowNode(row, top + i)),
          count,
        };
      }),
    );

    function toRowNode(row: T, groupIndex: number): GridRowNode<T> {
      if (groupConfig?.isGroup(row)) {
        return {
          key: groupConfig.getGroupKey(row),
          groupData: row,
          open: false,
          count: groupConfig.getGroupCount(row),
        };
      } else {
        return {
          groupKey: parent?.key,
          groupIndex,
          data: row,
          state: 'LOADED',
        };
      }
    }
  }
}
